import React, { useMemo } from 'react';
import { formatCurrency } from '@smartphone-tycoon/shared-ui';
import { formatCompactNumber } from '@smartphone-tycoon/shared-core';
import type { Product } from '@smartphone-tycoon/shared-core';
import { ProductMetricsService } from '@smartphone-tycoon/shared-core';

interface ProductCostsProps {
  products: Product[];
  dailyStats?: {
    revenue: number;
    costs: number;
    profit: number;
    productSales?: Array<{
      productId: string;
      unitsSold: number;
      revenue: number;
    }>;
  };
}

export function ProductCosts({ products, dailyStats }: ProductCostsProps) {
  // Calculate detailed cost breakdown for each product
  const productCostBreakdown = useMemo(() => {
    return products.map(product => {
      const components = product.designConfig?.components || {};
      const componentCost = ProductMetricsService.calculateProductionCost(components);
      
      // Find daily sales data for this product
      const dailySalesData = dailyStats?.productSales?.find(ps => ps.productId === product.id);
      const dailyUnitsSold = dailySalesData?.unitsSold || 0;
      const dailyRevenue = dailySalesData?.revenue || 0;
      
      // Calculate daily production costs
      const dailyProductionCost = dailyUnitsSold * product.productionCost;
      
      // Calculate monthly projections
      const monthlyUnitsSold = dailyUnitsSold * 30;
      const monthlyRevenue = dailyRevenue * 30;
      const monthlyProductionCost = dailyProductionCost * 30;
      const monthlyProfit = monthlyRevenue - monthlyProductionCost;
      
      return {
        product,
        componentCost,
        unitProductionCost: product.productionCost,
        dailyUnitsSold,
        dailyProductionCost,
        dailyRevenue,
        monthlyUnitsSold,
        monthlyProductionCost,
        monthlyRevenue,
        monthlyProfit,
        profitMargin: monthlyRevenue > 0 ? (monthlyProfit / monthlyRevenue) * 100 : 0
      };
    });
  }, [products, dailyStats]);

  // Calculate aggregate costs
  const aggregateCosts = useMemo(() => {
    const totalDailyProductionCost = productCostBreakdown.reduce((sum, p) => sum + p.dailyProductionCost, 0);
    const totalMonthlyProductionCost = productCostBreakdown.reduce((sum, p) => sum + p.monthlyProductionCost, 0);
    
    // Component purchase costs (one-time for each product)
    const totalComponentCosts = productCostBreakdown.reduce((sum, p) => sum + p.componentCost, 0);
    
    // R&D costs per product (estimate based on development time)
    const rdCostPerProduct = 50000; // Base R&D cost estimate
    const developmentStatuses = ['concept', 'design', 'development', 'manufacturing'];
    const developingProducts = products.filter(p => developmentStatuses.includes(p.status));
    const totalRDCosts = developingProducts.length * rdCostPerProduct;
    
    return {
      totalDailyProductionCost,
      totalMonthlyProductionCost,
      totalComponentCosts,
      totalRDCosts,
      activeProducts: products.filter(p => p.status === 'production').length,
      developingProducts: developingProducts.length
    };
  }, [productCostBreakdown, products]);

  return (
    <div className="space-y-6">
      {/* Detailed Cost Breakdown by Product */}
      <div className="bg-theme-bg-content rounded-lg shadow">
        <div className="p-4 border-b">
          <h3 className="text-lg font-semibold text-theme-text-primary">Product Cost Breakdown</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-theme-border">
            <thead className="bg-theme-bg-secondary">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-theme-text-muted uppercase tracking-wider">
                  Product
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-theme-text-muted uppercase tracking-wider">
                  Status
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-theme-text-muted uppercase tracking-wider">
                  Unit Cost
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-theme-text-muted uppercase tracking-wider">
                  Daily Units
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-theme-text-muted uppercase tracking-wider">
                  Daily Cost
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-theme-text-muted uppercase tracking-wider">
                  Monthly Cost
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-theme-text-muted uppercase tracking-wider">
                  Profit Margin
                </th>
              </tr>
            </thead>
            <tbody className="bg-theme-bg-content divide-y divide-theme-border">
              {productCostBreakdown.map(({ product, unitProductionCost, dailyUnitsSold, dailyProductionCost, monthlyProductionCost, profitMargin }) => (
                <tr key={product.id} className={product.status !== 'production' ? 'opacity-60' : ''}>
                  <td className="px-4 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-theme-text-primary">{product.name}</div>
                    <div className="text-xs text-theme-text-muted">Released: {product.releaseDate || 'Not yet'}</div>
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-right">
                    <span className={`inline-flex px-2 py-1 text-xs rounded-full ${
                      product.status === 'production'
                        ? 'bg-theme-success/20 text-theme-success'
                        : ['concept', 'design', 'development', 'manufacturing'].includes(product.status)
                        ? 'bg-theme-warning/20 text-theme-warning'
                        : 'bg-theme-bg-tertiary text-theme-text-primary'
                    }`}>
                      {product.status}
                    </span>
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-right text-sm text-theme-text-primary">
                    {formatCurrency(unitProductionCost)}
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-right text-sm text-theme-text-primary">
                    {formatCompactNumber(dailyUnitsSold)}
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-right text-sm text-theme-text-primary">
                    {formatCurrency(dailyProductionCost)}
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-right text-sm font-medium text-theme-text-primary">
                    {formatCurrency(monthlyProductionCost)}
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-right text-sm">
                    <span className={`font-medium ${profitMargin > 0 ? 'text-theme-success' : 'text-theme-error'}`}>
                      {profitMargin.toFixed(1)}%
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot className="bg-theme-bg-secondary">
              <tr>
                <td colSpan={4} className="px-4 py-3 text-right font-medium text-theme-text-primary">
                  Total
                </td>
                <td className="px-4 py-3 text-right font-bold text-theme-text-primary">
                  {formatCurrency(aggregateCosts.totalDailyProductionCost)}
                </td>
                <td className="px-4 py-3 text-right font-bold text-theme-text-primary">
                  {formatCurrency(aggregateCosts.totalMonthlyProductionCost)}
                </td>
                <td></td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>

      {/* Component Costs Detail */}
      <div className="bg-theme-bg-content rounded-lg shadow">
        <div className="p-4 border-b">
          <h3 className="text-lg font-semibold text-theme-text-primary">Component Inventory Costs</h3>
        </div>
        <div className="p-4">
          <div className="space-y-3">
            {productCostBreakdown.map(({ product, componentCost }) => (
              <div key={product.id} className="flex justify-between items-center py-2 border-b">
                <div>
                  <p className="font-medium text-theme-text-primary">{product.name}</p>
                  <p className="text-sm text-theme-text-muted">
                    Components: {Object.keys(product.designConfig?.components || {}).length}
                  </p>
                </div>
                <p className="font-medium text-theme-text-primary">{formatCurrency(componentCost)}</p>
              </div>
            ))}
            <div className="flex justify-between items-center py-2 font-bold">
              <p className="text-theme-text-primary">Total Component Inventory Value</p>
              <p className="text-theme-text-primary">{formatCurrency(aggregateCosts.totalComponentCosts)}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}